import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy } from 'react'
import type { ReactNode } from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'

import { ApiError } from './api/client'
import { AuthProvider } from './components/AuthProvider'
import { Layout } from './components/Layout'
import { Loading } from './components/StateViews'
import { useAuth } from './hooks/useAuth'
import { Companies } from './pages/Companies'
import { Intermediary } from './pages/Intermediary'
import { Login } from './pages/Login'
import { Movements } from './pages/Movements'
import { Search } from './pages/Search'
import { System } from './pages/System'
import type { Role } from './types'

// The two pages with charts pull in Recharts, which on its own weighs more than
// everything else: they load when they are needed, not on the login page. The
// Suspense boundary that catches them sits around the Outlet in Layout.
const Summary = lazy(() => import('./pages/Summary').then((m) => ({ default: m.Summary })))
const Company = lazy(() => import('./pages/Company').then((m) => ({ default: m.Company })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // Nothing to gain by insisting: a 401 is the silent renewal's business,
        // a 403 means the role is not enough, a 404 will not become a 200.
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) return false
        return failureCount < 2
      },
    },
  },
})

/** Keeps the destination, so after logging in one lands where one was going. */
function RequireAuth({ children, role }: { children: ReactNode; role?: Role }) {
  const { profile, loading, hasRole } = useAuth()
  const location = useLocation()

  if (loading) return <Loading rows={6} />
  if (!profile) {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
    )
  }
  if (role && !hasRole(role)) return <Navigate to="/" replace />
  return <>{children}</>
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route path="/" element={<Summary />} />
              <Route path="/movements" element={<Movements />} />
              <Route path="/companies" element={<Companies />} />
              <Route path="/companies/:code" element={<Company />} />
              <Route path="/intermediaries/:number" element={<Intermediary />} />
              <Route path="/search" element={<Search />} />
              <Route
                path="/system"
                element={
                  <RequireAuth role="admin">
                    <System />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  )
}
